import { FC, PropsWithChildren, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

import ChevronRightIcon from "@/components/ui/icons/ChevronRightIcon.tsx";
import LoadingButton from "@/components/functional/LoadingButton/LoadingButton.tsx";
import { useAuthContext } from "@/context/AuthContext.tsx";
import useUserUpdate from "@/api/users/useUserUpdate.ts";
import { isLegacyAvoidValue } from "@/utils/dna/avoidance.ts";
import { missingTranslationToReadable } from "@/utils/missingTranslationToReadable.ts";
import OnboardingStep from "$/_const/OnboardingStep.ts";
import Routes from "@/router/Routes.ts";
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import { usePostHog } from '@posthog/react';

import TravelDnaStep, {
  type TravelDnaStep as TravelDnaStepValue,
} from '@/_constants/TravelDnaStep.ts';

type SummaryCardProps = PropsWithChildren<{
  title: string;
}>;

const SummaryCard: FC<SummaryCardProps> = ({ title, children }) => (
  <section className="rounded-2xl border border-primary bg-white px-6 py-5 sm:px-7 sm:py-6">
    <div className="mb-3">
      <h2 className="font-feature text-2xl font-bold text-[#191919]">
        {title}
      </h2>
    </div>

    <div>{children}</div>
  </section>
);

type SummaryRowProps = {
  label: string;
  children: ReactNode;
  editLabel: string;
  onEdit: () => void;
  firm?: boolean;
  firmLabel?: string;
};

const SummaryRow: FC<SummaryRowProps> = ({
  label,
  children,
  editLabel,
  onEdit,
  firm = false,
  firmLabel,
}) => (
  <div className="flex items-center gap-2 border-b border-black/25 py-3 last:border-b-0">
    <div className="grid min-w-0 flex-1 gap-2 sm:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)] sm:items-center">
      <div className="flex flex-wrap items-center gap-2">
        <span className="font-semibold text-primary">{label}</span>

        {firm && firmLabel ? (
          <span className="rounded-full border border-primary bg-primary/10 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-primary">
            {firmLabel}
          </span>
        ) : null}
      </div>

      <div className="font-semibold text-[#191919] sm:text-right">
        {children}
      </div>
    </div>

    <button
      type="button"
      className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-primary transition-colors hover:bg-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      onClick={onEdit}
      title={`${editLabel}: ${label}`}
    >
      <PencilSquareIcon className="h-5 w-5" aria-hidden="true" />
      <span className="sr-only">
        {editLabel}: {label}
      </span>
    </button>
  </div>
);

type ChipListProps = {
  items: string[];
  emptyText: string;
  firm?: boolean;
};

const ChipList: FC<ChipListProps> = ({
  items,
  emptyText,
  firm = false,
}) => {
  if (items.length === 0) {
    return <span className="text-[#777]">{emptyText}</span>;
  }

  return (
    <div className="flex flex-wrap gap-2 sm:justify-end">
      {items.map((item, index) => (
        <span
          key={`${item}-${index}`}
          className={[
            "rounded-full border px-4 py-1.5 text-xs font-semibold",
            firm
              ? "border-primary bg-primary/15 text-primary"
              : "border-primary bg-[#F5F0E8] text-primary",
          ].join(" ")}
        >
          {item}
        </span>
      ))}
    </div>
  );
};

const OnboardingDnaConfirmationPage: FC = () => {
  const posthog = usePostHog();
  const { t, i18n } = useTranslation();
  const { user, isOnboarding } = useAuthContext();
  const navigate = useNavigate();
  const { mutateAsync: updateUser, isPending } = useUserUpdate();

  const dna = user?.dna?.data;
  const criticalRequirements = user?.dna?.critical_requirements;

  const noneSelected = t("dnaConfirmationPage.noneSelected");
  const firmRequirement = t("dnaConfirmationPage.firmRequirement");
  const editLabel = t("button.edit");

  const translateFirstExisting = (
    keys: string[],
    fallback: string,
  ): string => {
    const matchingKey = keys.find((key) => i18n.exists(key));

    return matchingKey ? String(t(matchingKey)) : fallback;
  };

  const translateDnaValue = (
    section: string,
    value: string,
  ): string =>
    translateFirstExisting(
      [`travelDnaPage.${section}.${value}`],
      missingTranslationToReadable(value),
    );

  const translateDetail = (
    section: string,
    category: string,
    value: string,
  ): string =>
    translateFirstExisting(
      [
        `travelDnaPage.${section}Details.${category}.${value}`,
        `travelDnaPage.${section}.${category}.${value}`,
        `travelDnaPage.${section}.${value}`,
      ],
      missingTranslationToReadable(value),
    );

  const homeItems =
    dna?.where_do_you_feel_most_at_home?.map((item) =>
      translateDnaValue("whereDoYouFeelMostAtHome", item)
    ) ?? [];

  const foodPreferenceItems = [
    ...(criticalRequirements?.food_preferences?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.foodPreferences.${item}`,
          `travelDnaPage.yourFoodPreferences.${item}`,
        ],
        missingTranslationToReadable(item),
      )
    ) ?? []),
    ...(criticalRequirements?.food_preferences_custom
      ? [criticalRequirements.food_preferences_custom]
      : []),
  ];

  const splurgeItems =
    dna?.what_do_you_splurge_on?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.whatDoYouSplurgeOn.${item}`,
          `travelDnaPage.yourSplurges.${item}`,
        ],
        missingTranslationToReadable(item),
      )
    ) ?? [];

  const avoidItems = [
    ...(dna?.what_should_avoid_when_planning?.flatMap((category) => {
      const details = dna.what_should_avoid_when_planning_details?.[category] ??
        [];

      if (details.length > 0) {
        return details.map((detail) =>
          translateDetail(
            "whatShouldAvoidWhenPlanning",
            category,
            detail,
          )
        );
      }

      return isLegacyAvoidValue(category)
        ? [translateDnaValue("whatShouldAvoidWhenPlanning", category)]
        : [];
    }) ?? []),
    ...(dna?.what_should_avoid_when_planning_custom
      ? [dna.what_should_avoid_when_planning_custom]
      : []),
  ];

  const accessibilityItems = [
    ...(criticalRequirements?.accessibility_requirements?.map((item) =>
      translateFirstExisting(
        [
          `travelDnaPage.accessibilityRequirements.${item}`,
          `travelDnaPage.accessAndSupport.${item}`,
        ],
        missingTranslationToReadable(item),
      )
    ) ?? []),
    ...(criticalRequirements?.accessibility_requirements_custom
      ? [criticalRequirements.accessibility_requirements_custom]
      : []),
  ];

  const lowerFirstCharacter = (value: string): string =>
    value.charAt(0).toLocaleLowerCase() + value.slice(1);

  const headlineInterest = dna?.what_makes_great_trip_for_you?.[0]
    ? translateDnaValue(
      "whatMakesGreatTripForYou",
      dna.what_makes_great_trip_for_you[0],
    )
    : null;

  const headlineExplore = dna?.how_do_you_like_to_explore
    ? translateDnaValue(
      "howDoYouLikeToExplore",
      dna.how_do_you_like_to_explore,
    )
    : null;

  const handleEdit = (step: TravelDnaStepValue) => {
    navigate(Routes.ONBOARDING__DNA, {
      state: {
        dnaEdit: {
          startStep: step,
          endStep: step,
          returnTo: Routes.ONBOARDING__DNA_CONFIRMATION,
        },
      },
    });
  };

  const handleGetStarted = () => {
    void (async () => {
      try {
        if (!user) return;

        if (isOnboarding) {
          await updateUser([
            { onboarding_step: OnboardingStep.TRIP_CREATION },
            { id: user.id },
          ]);

          posthog?.capture('onboarding_step_completed', {
            onboarding_step: 'dna_confirmation',
          });
        }

        navigate(Routes.ONBOARDING__TRIP_CREATE);
      } catch (error) {
        console.error(error);
        posthog?.capture('onboarding_step_failed', {
          onboarding_step: 'dna_confirmation',
        });
        posthog?.captureException(error, {
          operation: 'confirm_travel_dna',
          onboarding_step: 'dna_confirmation',
        });
      }
    })();
  };

  return (
    <div className="min-h-screen w-full bg-journi-theme-light">
      <header className="relative left-1/2 -mt-8 w-[100dvw] -translate-x-1/2 bg-journi-black px-6 py-10 sm:px-10 lg:px-16">
        <div className="mx-auto max-w-[1240px]">
          <p className="mb-4 text-sm font-bold uppercase tracking-wide text-primary">
            {t("dnaConfirmationPage.header")}
          </p>

          <h1 className="max-w-4xl font-feature text-4xl font-bold leading-tight text-journi-theme-light sm:text-5xl lg:text-6xl">
            {headlineInterest
              ? (
                <>
                  {t("dnaConfirmationPage.headlinePrefix")}{" "}
                  <span className="italic text-primary">
                    {lowerFirstCharacter(headlineInterest)}
                  </span>

                  {headlineExplore
                    ? (
                      <>
                        {" "}
                        {t("dnaConfirmationPage.headlineJoin")}{" "}
                        <span className="italic text-primary">
                          {lowerFirstCharacter(headlineExplore)}
                        </span>
                      </>
                    )
                    : null}
                  .
                </>
              )
              : headlineExplore
              ? (
                <>
                  {t("dnaConfirmationPage.headlineExploreOnly")}{" "}
                  <span className="italic text-primary">
                    {lowerFirstCharacter(headlineExplore)}
                  </span>
                  .
                </>
              )
              : (
                t("dnaConfirmationPage.fallbackHeadline")
              )}
          </h1>
        </div>
      </header>

      <main className="px-5 py-8 sm:px-8 lg:px-12 lg:py-10">
        <div className="mx-auto max-w-[1240px]">
          <div className="mx-auto mb-10 max-w-4xl text-center">
            <p className="text-sm font-semibold leading-7 text-text-secondary sm:text-base">
              {t("dnaConfirmationPage.subheader")}{" "}
              {t("dnaConfirmationPage.firmRequirementPrefix")}{" "}
              <span className="mx-1 inline-flex align-middle rounded-full border border-primary bg-primary/10 px-3 py-1 text-[10px] font-semibold uppercase leading-none tracking-wide text-primary">
                {firmRequirement}
              </span>{" "}
              {t("dnaConfirmationPage.firmRequirementHint")}
            </p>
          </div>

          <div className="flex flex-col gap-6">
            <SummaryCard title={t("dnaConfirmationPage.howYouTravel")}>
              <SummaryRow
                label={t("dnaConfirmationPage.pace")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_PACE)}
              >
                {dna?.how_do_you_like_to_travel
                  ? translateDnaValue(
                    "howDoYouLikeToTravel",
                    dna.how_do_you_like_to_travel,
                  )
                  : noneSelected}
              </SummaryRow>

              <SummaryRow
                label={t("dnaConfirmationPage.accommodationStyle")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_STAY)}
              >
                {dna?.what_type_of_place_do_you_prefer
                  ? translateDnaValue(
                    "whatTypeOfPlaceDoYouPrefer",
                    dna.what_type_of_place_do_you_prefer,
                  )
                  : noneSelected}
              </SummaryRow>

              <SummaryRow
                label={t("dnaConfirmationPage.feelsMostLikeHome")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_SANCTUARY)}
              >
                <ChipList
                  items={homeItems}
                  emptyText={noneSelected}
                />
              </SummaryRow>

            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.atTheTable")}>
              <SummaryRow
                label={t("dnaConfirmationPage.whenEatingOut")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_TABLE)}
              >
                {dna?.your_approach_to_food_when_you_travel
                  ? translateDnaValue(
                    "yourApproachToFoodWhenYouTravel",
                    dna.your_approach_to_food_when_you_travel,
                  )
                  : noneSelected}
              </SummaryRow>

              <SummaryRow
                label={t("dnaConfirmationPage.foodPreferences")}
                editLabel={editLabel}
                onEdit={() =>
                  handleEdit(TravelDnaStep.YOUR_FOOD_PREFERENCES)}
                firm={foodPreferenceItems.length > 0}
                firmLabel={firmRequirement}
              >
                <ChipList
                  items={foodPreferenceItems}
                  emptyText={noneSelected}
                  firm
                />
              </SummaryRow>

              {criticalRequirements?.food_allergy_details
                ? (
                  <SummaryRow
                    label={t("dnaConfirmationPage.allergies")}
                    editLabel={editLabel}
                    onEdit={() =>
                      handleEdit(TravelDnaStep.YOUR_FOOD_PREFERENCES)}
                    firm
                    firmLabel={firmRequirement}
                  >
                    {criticalRequirements.food_allergy_details}
                  </SummaryRow>
                )
                : null}
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.howYouExplore")}>
              <SummaryRow
                label={t("dnaConfirmationPage.onceThere")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_WORLD)}
              >
                {dna?.how_do_you_like_to_explore
                  ? translateDnaValue(
                    "howDoYouLikeToExplore",
                    dna.how_do_you_like_to_explore,
                  )
                  : noneSelected}
              </SummaryRow>
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.planningAndMoney")}>
              <SummaryRow
                label={t("dnaConfirmationPage.howMuchYouPlan")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_PLANS)}
              >
                {dna?.how_much_do_you_like_a_plan != null
                  ? t(
                    `travelDnaPage.yourPlans.captions.${dna.how_much_do_you_like_a_plan}`,
                  )
                  : noneSelected}
              </SummaryRow>

              <SummaryRow
                label={t("dnaConfirmationPage.spendingStyle")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_SPENDING)}
              >
                {dna?.spending_preference != null
                  ? t(
                    `travelDnaPage.yourSpending.captions.${dna.spending_preference}`,
                  )
                  : noneSelected}
              </SummaryRow>

              <SummaryRow
                label={t("dnaConfirmationPage.worthTheSplurge")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_SPLURGES)}
              >
                <ChipList
                  items={splurgeItems}
                  emptyText={noneSelected}
                />
              </SummaryRow>
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.whatYouLove")}>
              {dna?.what_makes_great_trip_for_you?.length
                ? (
                  dna.what_makes_great_trip_for_you.map((category) => {
                    const details =
                      dna.what_makes_great_trip_for_you_details?.[category] ??
                        [];

                    return (
                      <SummaryRow
                        key={category}
                        label={translateDnaValue(
                          "whatMakesGreatTripForYou",
                          category,
                        )}
                        editLabel={editLabel}
                        onEdit={() => handleEdit(TravelDnaStep.YOUR_TRIP)}
                      >
                        {details.length > 0
                          ? (
                            <ChipList
                              items={details.map((detail) =>
                                translateDetail(
                                  "whatMakesGreatTripForYou",
                                  category,
                                  detail,
                                )
                              )}
                              emptyText={noneSelected}
                            />
                          )
                          : (
                            <span className="text-[#777]">
                              {t("dnaConfirmationPage.generalInterestOnly")}
                            </span>
                          )}
                      </SummaryRow>
                    );
                  })
                )
                : (
                  <SummaryRow
                    label={t("dnaConfirmationPage.interests")}
                    editLabel={editLabel}
                    onEdit={() => handleEdit(TravelDnaStep.YOUR_TRIP)}
                  >
                    <span className="text-[#777]">{noneSelected}</span>
                  </SummaryRow>
                )}

              {dna?.what_makes_great_trip_for_you_custom
                ? (
                  <SummaryRow
                    label={t("dnaConfirmationPage.somethingElse")}
                    editLabel={editLabel}
                    onEdit={() => handleEdit(TravelDnaStep.YOUR_TRIP)}
                  >
                    {dna.what_makes_great_trip_for_you_custom}
                  </SummaryRow>
                )
                : null}
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.whatToAvoid")}>
              <SummaryRow
                label={t("dnaConfirmationPage.codyWillAvoid")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_DEFAULTS)}
              >
                <ChipList
                  items={avoidItems}
                  emptyText={noneSelected}
                />
              </SummaryRow>
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.accessAndSupport")}>
              <SummaryRow
                label={t("dnaConfirmationPage.supportRequirements")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.YOUR_ACCESSIBILITY)}
                firm={accessibilityItems.length > 0}
                firmLabel={firmRequirement}
              >
                <ChipList
                  items={accessibilityItems}
                  emptyText={noneSelected}
                  firm
                />
              </SummaryRow>
            </SummaryCard>

            <SummaryCard title={t("dnaConfirmationPage.theHumanBit")}>
              <SummaryRow
                label={t("dnaConfirmationPage.whatCodyShouldKnow")}
                editLabel={editLabel}
                onEdit={() => handleEdit(TravelDnaStep.ANYTHING_ELSE)}
              >
                {criticalRequirements?.anything_else_journi_should_know
                  ? (
                    criticalRequirements.anything_else_journi_should_know
                  )
                  : (
                    <span className="text-[#777]">
                      {t("dnaConfirmationPage.nothingAdded")}
                    </span>
                  )}
              </SummaryRow>
            </SummaryCard>
          </div>

          <div className="flex flex-col items-center pb-12 pt-8">
            <p className="mb-6 max-w-3xl text-center text-sm font-semibold text-primary">
              {t("dnaConfirmationPage.savedHint")}
            </p>

            <LoadingButton
              endIcon={<ChevronRightIcon className="h-3.5 w-3.5" />}
              type="button"
              color="primary"
              loading={isPending}
              className="journi-primary-button w-full sm:w-[376px]"
              onClick={handleGetStarted}
            >
              {t("button.startPlanningMyTrip")}
            </LoadingButton>
          </div>
        </div>
      </main>
    </div>
  );
};

export default OnboardingDnaConfirmationPage;
